/**
 * Side-effect module that registers all field-app offline-queue operation
 * handlers at app boot.
 *
 * Imported once from entry-client.tsx (`import './field/offline-queue/registerHandlers.js'`).
 *
 * Each operation key here must match the `operation` value passed to
 * useFieldMutation at the call site. When the device is offline, the queue
 * persists a payload tagged with the key; when it reconnects, the matching
 * handler replays the API call.
 *
 * Convention: `<slice>.<verb>` (e.g. 'inventory.changeLocation').
 */

import { activitiesKeys } from '../../api/generated_hooks/generated-activities-hooks.js'
import { activitiesNotesJunctionKeys } from '../../api/generated_hooks/generated-activities_notes_junction-hooks.js'
import { httpClient } from '../../api/http-client.js'
import { createActivityNote } from '../shared/noteUpload.js'
import {
  buildPhotoUpload,
  fileFromBytes,
  PhotoUploadBuildError,
  type PhotoUploadTarget,
} from '../shared/photoUpload.js'

import { getAppQueryClient } from './appQueryClient.js'
import { registerOperation } from './registry.js'
import type { ReplayResult } from './types.js'

// Phase 0 stub — kept around so /field/sync can be smoke-tested with a no-op.
registerOperation<{ message?: string }>('noop.echo', async (op) => {
  console.log('[offline-queue] noop.echo replayed', op.payload)
  return { ok: true }
})

// ─── Slice A: Warehouse ──────────────────────────────────────────────────
//
// All warehouse mutations are PUTs against /api/v1/inventory-items/:id with
// a partial payload. The handler shape is identical for each — we use a
// helper to register them DRY-ly.

interface InventoryUpdatePayload {
  id: string
  patch: Record<string, unknown>
}

async function replayInventoryPut(
  payload: InventoryUpdatePayload,
): Promise<ReplayResult> {
  try {
    await httpClient.put(`/inventory-items/${payload.id}`, payload.patch)
    return { ok: true }
  } catch (err) {
    return classifyReplayError(err)
  }
}

/** Classify an error from httpClient as retryable (network/5xx) vs not (4xx). */
function classifyReplayError(err: unknown): ReplayResult {
  const message = err instanceof Error ? err.message : String(err)
  // httpClient throws errors of the form "HTTP 404: Not Found" — pull the code.
  const match = message.match(/^HTTP (\d{3}):/)
  if (match) {
    const status = Number(match[1])
    if (status >= 400 && status < 500) {
      return { ok: false, retryable: false, error: message }
    }
  }
  // Network errors / 5xx / unknown — try again later.
  return { ok: false, retryable: true, error: message }
}

// useFieldMutation packs the user-supplied vars into `payload` directly.
// Each warehouse mutation packs different fields, so we adapt them here:

registerOperation<{ id: string; locationId: string; locationName: string }>(
  'inventory.changeLocation',
  (op) => replayInventoryPut({ id: op.payload.id, patch: { inventory_location_id: op.payload.locationId } }),
)

registerOperation<{ id: string; quantity: number }>(
  'inventory.adjustQuantity',
  (op) => replayInventoryPut({ id: op.payload.id, patch: { current_quantity: op.payload.quantity } }),
)

registerOperation<{ id: string; locationId: string; statusId: string }>(
  'inventory.checkIn',
  (op) => replayInventoryPut({
    id: op.payload.id,
    patch: {
      inventory_location_id: op.payload.locationId,
      inventory_status_id: op.payload.statusId,
    },
  }),
)

// inventory.updateStatus — set an item's inventory_status_id. Used by the
// cycle-count reconciliation (client/field/warehouse/cycle-count/*) to
// categorize items the system expected in a section but that weren't
// scanned: mark them Completed install (CONSUMED), Missing (removed), or
// Pending return (PENDING_RETURN). `statusName` is carried only for the
// human-readable /field/sync description; the replay uses statusId.
registerOperation<{ id: string; statusId: string; statusName?: string }>(
  'inventory.updateStatus',
  (op) => replayInventoryPut({ id: op.payload.id, patch: { inventory_status_id: op.payload.statusId } }),
)

// ─── Slice B: Jobsite ────────────────────────────────────────────────────
//
// photo.upload — the OFFLINE fallback path (card T8u-BVfkbArp). When the device
// is online the capture flow uploads the FRESH File directly (see
// client/field/shared/photoUpload.ts uploadPhotoDirect); this handler only runs
// when the direct upload couldn't reach the server, so the photo was queued.
//
// BYTE-SAFE STORAGE: the payload carries the image as an ArrayBuffer (`bytes`),
// NOT a raw Blob. Raw Blobs stored in IndexedDB come back EMPTY on iOS
// Safari/PWA (a WebKit bug) → the replayed multipart had no file bytes → the
// server 400'd with "No file provided". Storing bytes and reconstructing a File
// here at replay time defeats that bug so the queued path actually sends a file.
//
// Targets (resolved in the shared builder):
//   - lot       → POST /api/v1/workflows/lot-attachments/upload (lot_id)
//   - area      → parent lot's id + `[Area: <name>]` caption tag
//   - inventory → POST /api/v1/workflows/inventory-photos/upload
//
// Per stakeholder decision: NO compression / NO size cap; we send the raw bytes
// the user captured. The runner paces retries with exponential backoff.

interface PhotoUploadPayload {
  target: PhotoUploadTarget
  /**
   * Owning activity for a field-app completion photo (card 4_x_b5Smhsld).
   * When present, persisted to lot_attachments.activity_id so the activity
   * photo views + the B1 photo-required gate can scope to this activity.
   * Absent for plain lot/inventory captures.
   */
  activityId?: string
  /**
   * Area this field photo was assigned to AT CAPTURE (card eYaw2jj1_Bwx / F1).
   * Frozen into the payload at enqueue time so an offline photo replays with the
   * exact area the worker picked. Persisted to lot_attachments.area_id. For an
   * 'area' target the area is implied by the target itself.
   */
  areaId?: string
  /**
   * Image BYTES (not a Blob — see the byte-safe note above). Reconstructed into
   * a File at replay time so the multipart part is never empty on iOS.
   */
  bytes: ArrayBuffer
  filename: string
  mimeType: string
}

registerOperation<PhotoUploadPayload>('photo.upload', async (op) => {
  const { target, bytes, filename, mimeType } = op.payload
  const activityId = op.payload.activityId ?? ('activityId' in target ? target.activityId : undefined)
  // Capture-time area (card eYaw2jj1_Bwx): replay with the SAME area the worker
  // picked. Fall back to the target's own area for an 'area' target.
  const areaId = op.payload.areaId ?? (target.kind === 'area' ? target.areaId : undefined)

  // Rebuild a File from the stored bytes (defeats the iOS IndexedDB blob-loss
  // bug), then hand it to the SAME request builder the direct path uses so the
  // two paths send an identical body.
  const file = fileFromBytes(bytes, filename, mimeType)

  let built
  try {
    built = await buildPhotoUpload({ target, activityId, areaId, file })
  } catch (err) {
    if (err instanceof PhotoUploadBuildError) {
      return { ok: false, retryable: err.retryable, error: err.message }
    }
    return { ok: false, retryable: true, error: err instanceof Error ? err.message : String(err) }
  }

  try {
    const response = await fetch(built.url, {
      method: 'POST',
      credentials: 'include',
      body: built.formData,
    })
    if (!response.ok) {
      const text = await response.text().catch(() => response.statusText)
      const message = `HTTP ${response.status}: ${text}`
      if (response.status >= 400 && response.status < 500) {
        return { ok: false, retryable: false, error: message }
      }
      return { ok: false, retryable: true, error: message }
    }
    return { ok: true }
  } catch (err) {
    return { ok: false, retryable: true, error: err instanceof Error ? err.message : String(err) }
  }
})

// PO receive — the call site stashes the full POST body + a stable
// idempotency-key in the payload so retries are safe (the server dedupes).
// `body` is forwarded verbatim (JSON.stringify below), so it's safe for
// call sites to include additional per-line-item fields — e.g. the field
// warehouse receive page's `numberOfItems` / `serializedItems` (per-slab
// dims + location, see client/field/warehouse/ReceivePage.tsx) — without
// touching this handler. The type here documents the superset of fields
// call sites may send.
//
// Note: this endpoint is at /api/receive-purchase-order/:id (no /v1 prefix),
// so we bypass httpClient (which prepends /api/v1) and use fetch directly.
registerOperation<{
  poId: string
  body: {
    lineItems: Array<{
      lineItemId: string
      quantityToReceive: number
      numberOfItems?: number
      notes?: string
      serializedItems?: Array<{
        length: number | null
        width: number | null
        thickness: string | null
        finish?: string | null
        block_number?: string | null
        inventory_location_id?: string | null
      }>
    }>
    globalNotes?: string
  }
  idempotencyKey: string
}>('inventory.receivePO', async (op) => {
  try {
    const response = await fetch(`/api/receive-purchase-order/${op.payload.poId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': op.payload.idempotencyKey,
      },
      body: JSON.stringify(op.payload.body),
    })
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText)
      const message = `HTTP ${response.status}: ${errorText}`
      // 4xx → permanent failure (validation, idempotency conflict, auth);
      // 5xx / network → retryable.
      if (response.status >= 400 && response.status < 500) {
        return { ok: false, retryable: false, error: message }
      }
      return { ok: false, retryable: true, error: message }
    }
    return { ok: true }
  } catch (err) {
    // Network failure (offline mid-replay).
    return { ok: false, retryable: true, error: err instanceof Error ? err.message : String(err) }
  }
})

// ─── Slice C: Hardware scanner substrate ────────────────────────────────
//
// barcode.record — logs every dispatched scan (hardware wedge, camera, or
// manual entry — see client/field/scan/ScanProvider.tsx) to
// barcode_scan_history via POST /api/v1/barcode-scan-history. Purely a
// history/audit trail; the scan pipeline itself (lookup/navigate or the
// active page consumer) never waits on this — recordScan.ts fires it and
// forgets. organization_id and scanned_by aren't known at the call site
// (ScanProvider has no auth context reference), so — mirroring the
// getOrganizationId() pattern already used above for photo.upload — we
// resolve both from GET /auth/me at replay time.
//
// device_id / app_version are left null: no device-identity concept exists
// yet in the field app (would need a persisted per-install UUID + a build
// version constant, neither of which exist today). TODO once device
// identity lands, populate them here — the barcode_scan_history schema
// already has the columns.

interface BarcodeRecordPayload {
  barcode_data: string
  scan_type: string
  source: string
  offline_scan_id: string
}

async function getCurrentUserId(): Promise<string | null> {
  try {
    const response = await httpClient.get<{ user?: { id?: string } }>('/auth/me')
    return response?.user?.id ?? null
  } catch {
    return null
  }
}

// Resolve the current org from /auth/me. photo.upload resolves its own org inside
// the shared builder (client/field/shared/photoUpload.ts); barcode.record still
// needs one here.
async function getOrganizationId(): Promise<string | null> {
  try {
    const response = await httpClient.get<{ data?: { organizationId?: string }; user?: { organizationId?: string } }>(
      '/auth/me',
    )
    return (
      (response as any)?.data?.organizationId
      ?? (response as any)?.user?.organizationId
      ?? (response as any)?.organizationId
      ?? null
    )
  } catch {
    return null
  }
}

registerOperation<BarcodeRecordPayload>('barcode.record', async (op) => {
  const [organizationId, userId] = await Promise.all([getOrganizationId(), getCurrentUserId()])
  if (!organizationId) {
    // Can't resolve org — retry later rather than dropping the scan record.
    return { ok: false, retryable: true, error: 'Could not resolve organization for barcode.record (auth)' }
  }

  try {
    await httpClient.post('/barcode-scan-history', {
      organization_id: organizationId,
      barcode_data: op.payload.barcode_data,
      scan_type: op.payload.scan_type,
      scan_timestamp: new Date(op.enqueuedAt).toISOString(),
      scanned_by: userId,
      offline_scan_id: op.payload.offline_scan_id,
      notes: `source: ${op.payload.source}`,
    })
    return { ok: true }
  } catch (err) {
    return classifyReplayError(err)
  }
})

// ─── Slice D: Field install (installer completion capture) ───────────────
//
// activity.setStatus — an installer marks an assigned activity Complete or
// Incomplete from /field/install. We PUT the new status_id onto the activity
// plus the free-text reason (verification_notes). completed_date /
// completed_by are set only when moving to the terminal (Completed) status,
// and cleared when re-opening; the caller resolves the correct terminal /
// non-terminal status_id per the activity's category before enqueueing
// (see client/field/install/useActivityStatusTargets.ts), so this handler is
// a thin, category-agnostic PUT. `completedBy` is resolved lazily from
// /auth/me at replay time when it isn't supplied, mirroring photo.upload.
interface ActivitySetStatusPayload {
  activityId: string
  statusId: string
  /** Whether this transition is the terminal "Completed" state. */
  isComplete: boolean
  /** Free-text reason written to verification_notes (optional). */
  reason?: string
  /** user_profile id credited as completer; resolved from /auth/me if absent. */
  completedBy?: string | null
}

registerOperation<ActivitySetStatusPayload>('activity.setStatus', async (op) => {
  const { activityId, statusId, isComplete, reason } = op.payload
  const completedBy = op.payload.completedBy ?? (isComplete ? await getCurrentUserId() : null)

  const patch: Record<string, unknown> = {
    status_id: statusId,
    // Only overwrite verification_notes when the installer typed a reason,
    // so re-opening without a note doesn't wipe an existing office note.
    ...(reason !== undefined ? { verification_notes: reason || null } : {}),
    completed_date: isComplete ? new Date(op.enqueuedAt).toISOString() : null,
    completed_by: isComplete ? completedBy : null,
  }

  try {
    await httpClient.put(`/activities/${activityId}`, patch)
    // Symmetry with the online path (card rKkWHT_XhbXU, part C): when a queued
    // completion replays on reconnect, invalidate the affected activity so the
    // field app's views reconcile with the server. The payload doesn't carry
    // the lot id, so we also invalidate the activities lists prefix (all
    // activities lists) — activitiesKeys.detail is the precise key; .lists()
    // covers the "My Activities" / lot lists without needing the lot id here.
    const qc = getAppQueryClient()
    if (qc) {
      void qc.invalidateQueries({ queryKey: activitiesKeys.detail(activityId) })
      void qc.invalidateQueries({ queryKey: activitiesKeys.lists() })
    }
    return { ok: true }
  } catch (err) {
    return classifyReplayError(err)
  }
})

// activity.completeWithCascade — complete a PRODUCTION-STAGE activity (CAD,
// PROGRAMMING, SAW, LAMINATION, FABRICATION) through the server cascade endpoint
// so the production auto-complete cascade fires (bug B1). The endpoint completes
// the primary activity AND back-fills every incomplete upstream stage in the same
// area. This is the SAME transport the web Production Tracker
// (client/components/ProductionTracker/hooks/useCompleteActivity.ts) and the
// field Production hub (client/field/production/useProductionQueue.ts) already
// use — those paths cascade correctly. This op routes the two FIELD paths that
// previously used the thin activity.setStatus PUT (which BYPASSES the cascade):
// the warehouse claim flow (client/field/warehouse/WarehouseMyWorkPage.tsx) and
// install/My-Activities completion (client/field/install/InstallActivityPage.tsx,
// production-stage activities only — install stays on activity.setStatus + its
// user-confirmed dialog). Queue-symmetric with activity.setStatus so a completion
// survives a dead-zone connection and cascades on replay.
//
// NOTE: the endpoint MUST be the call that completes the primary — it returns
// early WITHOUT cascading if the activity is already complete, so we never
// PUT-then-POST. The call sites keep an optimistic flip for the PRIMARY row; the
// upstream back-filled rows reconcile on the query invalidation/refetch below.
//
// FUTURE (Option 2, deferred): move the cascade server-side onto the generic
// activity update (a DB trigger / post-update hook on activities.status_id) so
// EVERY completion path cascades regardless of endpoint. Deferred for blast
// radius — it touches ~every activity-write site, would double-cascade with the
// existing endpoint callers, and risks recursion with the existing
// trg_activity_sqft_credits trigger.
interface ActivityCompleteWithCascadePayload {
  activityId: string
  /** user_profile id credited as completer (endpoint requires it). */
  completedBy: string
}

registerOperation<ActivityCompleteWithCascadePayload>('activity.completeWithCascade', async (op) => {
  const { activityId, completedBy } = op.payload
  try {
    // httpClient prefixes /api/v1, so this hits /api/v1/workflows/production-
    // cascade-completion — identical to useCompleteActivity / useProductionQueue.
    await httpClient.post('/workflows/production-cascade-completion', {
      activity_id: activityId,
      completed_by: completedBy,
    })
    // Same invalidation as activity.setStatus: reconcile the primary activity +
    // all activities lists (the upstream back-filled rows show up on refetch).
    const qc = getAppQueryClient()
    if (qc) {
      void qc.invalidateQueries({ queryKey: activitiesKeys.detail(activityId) })
      void qc.invalidateQueries({ queryKey: activitiesKeys.lists() })
    }
    return { ok: true }
  } catch (err) {
    return classifyReplayError(err)
  }
})

// activity.autoComplete — back-fill an EARLIER activity in the same area when
// an installer confirms the "Auto-Complete Earlier Activities?" dialog on the
// field completion path (card A21-backchain). Mirrors the web batch back-fill
// in ClickableActivityStatusBadge: the candidate moves to the same terminal
// status the installer chose, tagged was_autocompleted=true and NOT credited to
// a completer (completed_by stays null). square_footage_claimed follows whether
// the candidate itself is assigned, so sqft rollups stay consistent with the
// online path. Queue-symmetric with activity.setStatus so a confirmed back-fill
// survives an offline jobsite and replays on reconnect.
interface ActivityAutoCompletePayload {
  activityId: string
  statusId: string
  /** Whether the candidate has an assignment (drives square_footage_claimed). */
  squareFootageClaimed: boolean
  /** Candidate display name, for the /field/sync description only. */
  activityName?: string
}

registerOperation<ActivityAutoCompletePayload>('activity.autoComplete', async (op) => {
  const { activityId, statusId, squareFootageClaimed } = op.payload

  const patch: Record<string, unknown> = {
    status_id: statusId,
    completed_date: new Date(op.enqueuedAt).toISOString(),
    completed_by: null,
    was_autocompleted: true,
    square_footage_claimed: squareFootageClaimed,
  }

  try {
    await httpClient.put(`/activities/${activityId}`, patch)
    const qc = getAppQueryClient()
    if (qc) {
      void qc.invalidateQueries({ queryKey: activitiesKeys.detail(activityId) })
      void qc.invalidateQueries({ queryKey: activitiesKeys.lists() })
    }
    return { ok: true }
  } catch (err) {
    return classifyReplayError(err)
  }
})

// activity.setFollowUp — a field worker ticks "Needs follow-up" in the note UI
// (F7). Sets the needs_follow_up FLAG on the activity (composes with the
// Completed status; not a status). Queue-symmetric with the other activity
// writes so it survives a dead-zone jobsite and replays on reconnect. The
// "why" is the free-text note (verification_notes / the activity note), not a
// separate column — this op only flips the flag.
interface ActivitySetFollowUpPayload {
  activityId: string
  /** Display name of the activity, for the /field/sync description only. */
  activityName?: string
}

registerOperation<ActivitySetFollowUpPayload>('activity.setFollowUp', async (op) => {
  try {
    await httpClient.put(`/activities/${op.payload.activityId}`, { needs_follow_up: true })
    const qc = getAppQueryClient()
    if (qc) {
      void qc.invalidateQueries({ queryKey: activitiesKeys.detail(op.payload.activityId) })
      void qc.invalidateQueries({ queryKey: activitiesKeys.lists() })
    }
    return { ok: true }
  } catch (err) {
    return classifyReplayError(err)
  }
})

// activity.createFollowUp — a field worker taps "Create follow-up" on an
// activity (card X9V4OsWz-Bh1). ONE workflow endpoint atomically spawns a
// "Customer Service" CHILD activity (parented to this activity, scheduled today,
// open status), copies the parent's assignees onto it, saves the typed text as a
// note ON THE CHILD, and flags THIS activity needs_follow_up=true so the office
// ?followup=1 filter surfaces it. Queue-symmetric with the other activity writes
// so a follow-up reported on a dead-zone jobsite survives and replays on
// reconnect. The typed reason rides in the byte-free JSON payload.
interface ActivityCreateFollowUpPayload {
  parentActivityId: string
  /** Free-text reason (e.g. "seam touch up"); saved as a note on the child. */
  note?: string
  /** Display name of the parent activity, for the /field/sync description only. */
  activityName?: string
}

registerOperation<ActivityCreateFollowUpPayload>('activity.createFollowUp', async (op) => {
  try {
    // httpClient prefixes /api/v1 → /api/v1/workflows/activities/create-follow-up.
    await httpClient.post('/workflows/activities/create-follow-up', {
      parent_activity_id: op.payload.parentActivityId,
      note: op.payload.note ?? null,
    })
    // The parent is now flagged + a child exists — reconcile the parent detail
    // and every activities list (the new child shows up on refetch).
    const qc = getAppQueryClient()
    if (qc) {
      void qc.invalidateQueries({ queryKey: activitiesKeys.detail(op.payload.parentActivityId) })
      void qc.invalidateQueries({ queryKey: activitiesKeys.lists() })
    }
    return { ok: true }
  } catch (err) {
    return classifyReplayError(err)
  }
})

// note.createForActivity — a field worker adds a note to an activity, either at
// completion (the reason field, card 0cEzrRKvCW1D) or standalone via the
// "Add note" affordance. Notes attach the SAME way all other activity notes do:
// POST /api/v1/workflows/notes/create-with-activity inserts a notes row + an
// activities_notes_junction link, so the note shows in the web Notes tab and
// everywhere activity notes render. We NO LONGER write note text to
// activities.verification_notes from the field path — one home for notes.
//
// The payload is byte-free JSON (plain text) — no iOS IndexedDB blob-loss
// concern like the photo path — so it queues/replays verbatim. organization_id
// is resolved from /auth/me inside the shared builder at replay time.
interface NoteCreateForActivityPayload {
  activityId: string
  content: string
  noteType?: string
}

registerOperation<NoteCreateForActivityPayload>('note.createForActivity', async (op) => {
  const result = await createActivityNote({
    activityId: op.payload.activityId,
    content: op.payload.content,
    noteType: op.payload.noteType,
  })
  if (!result.ok) {
    return { ok: false, retryable: result.retryable, error: result.error }
  }
  // Refresh the activity's notes list so the field view + web Notes tab
  // reconcile once a queued note lands. Keyed on the same filter the list hook
  // uses (activity_id), matching ActivityNotes.tsx / the field note display.
  const qc = getAppQueryClient()
  if (qc) {
    void qc.invalidateQueries({
      queryKey: activitiesNotesJunctionKeys.list({ activity_id: op.payload.activityId }),
    })
  }
  return { ok: true }
})
